<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import MessageItem from './MessageItem.vue';
import {
  buildTimeline,
  channelColor,
  channelDisplayName,
  dayLabel,
} from '../helpers/timeline';

const props = defineProps({
  messages: { type: Array, required: true },
  channel: { type: Object, required: true },
  canPin: { type: Boolean, default: false },
  canReply: { type: Boolean, default: true },
  statusFor: { type: Function, required: true },
  currentUserId: { type: Number, default: null },
  receipts: { type: Object, default: () => ({}) },
  nameFor: { type: Function, required: true },
  hasMore: { type: Boolean, default: false },
  isLoadingOlder: { type: Boolean, default: false },
});

const emit = defineEmits(['star', 'pin', 'react', 'reply', 'loadOlder', 'info']);

const { t, locale } = useI18n();

// Follow new messages only while the reader is already near the bottom, so
// scrolling up to read history is not yanked back down.
const NEAR_BOTTOM_PX = 160;
const LOAD_OLDER_PX = 120;

const HIGHLIGHT_MS = 2500;

const listRef = ref(null);
const highlightedId = ref(null);
let highlightTimer = null;

// From a quote to the original message, if it is loaded.
// Returns whether the message was found (the search loads older pages and
// tries again; a quote click just warns).
const jumpTo = (messageId, { quiet = false } = {}) => {
  const el = listRef.value?.querySelector(`[data-message-id="${messageId}"]`);
  if (!el) {
    if (!quiet) useAlert(t('TEAM_CHAT.ORIGINAL_NOT_LOADED'));
    return false;
  }
  stickToBottom = false;
  // After the browser lays out (and any older page has been put in place),
  // or the position adjustment of that page would undo this scroll.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      highlightedId.value = messageId;
      clearTimeout(highlightTimer);
      highlightTimer = setTimeout(
        () => (highlightedId.value = null),
        HIGHLIGHT_MS
      );
    })
  );
  return true;
};
const contentRef = ref(null);
// Whether the reader is at the bottom: content that grows later (an image that
// finishes loading) keeps them there.
let stickToBottom = true;
// Height before older messages are prepended, to keep the reader in place.
let heightBeforeOlder = null;
const timeline = computed(() => buildTimeline(props.messages));

const scrollToBottom = () => {
  nextTick(() => {
    const el = listRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
};

const isNearBottom = () => {
  const el = listRef.value;
  return !el || el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
};

watch(
  () => props.channel.id,
  () => {
    stickToBottom = true;
    scrollToBottom();
  },
  { immediate: true }
);

useResizeObserver(contentRef, () => {
  if (stickToBottom && heightBeforeOlder === null) scrollToBottom();
});

const onScroll = () => {
  stickToBottom = isNearBottom();
  const el = listRef.value;
  if (!el || el.scrollTop > LOAD_OLDER_PX) return;
  if (!props.hasMore || props.isLoadingOlder) return;
  heightBeforeOlder = el.scrollHeight;
  emit('loadOlder');
};

watch(
  () => props.messages.length,
  (length, previous) => {
    if (heightBeforeOlder !== null) {
      const before = heightBeforeOlder;
      heightBeforeOlder = null;
      nextTick(() => {
        const el = listRef.value;
        if (el) el.scrollTop += el.scrollHeight - before;
      });
      return;
    }
    if (!previous || isNearBottom()) scrollToBottom();
  }
);

// A page that brought nothing new must not leave the marker behind (the next
// realtime message would be treated as history).
watch(
  () => props.isLoadingOlder,
  loading => {
    if (!loading) nextTick(() => (heightBeforeOlder = null));
  }
);

// Older pages loaded by code (looking for a search result) keep the reader in
// place, exactly like scrolling up does.
const prepareOlder = () => {
  stickToBottom = false;
  heightBeforeOlder = listRef.value?.scrollHeight ?? null;
};

defineExpose({ scrollToBottom, jumpTo, prepareOlder });
</script>

<template>
  <div
    ref="listRef"
    class="flex-1 min-h-0 px-5 pb-4 overflow-y-auto"
    @scroll.passive="onScroll"
  >
    <div ref="contentRef" class="flex flex-col justify-end min-h-full">
      <div v-if="hasMore" class="flex justify-center py-4">
        <span
          v-if="isLoadingOlder"
          class="size-5 i-lucide-loader-circle animate-spin text-n-slate-10"
        />
      </div>
      <div v-else class="pt-10 pb-4">
        <span
          class="flex items-center justify-center mb-3 rounded-2xl size-14"
          :class="channelColor(channel)"
        >
          <span
            class="size-6"
            :class="channel.private ? 'i-lucide-lock' : 'i-lucide-hash'"
          />
        </span>
        <h3 class="text-xl font-semibold text-n-slate-12">
          {{ t('TEAM_CHAT.WELCOME_TITLE', { name: channelDisplayName(channel) }) }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ channel.description || t('TEAM_CHAT.WELCOME_TEXT') }}
        </p>
      </div>

      <template v-for="item in timeline" :key="item.key">
        <div v-if="item.type === 'day'" class="flex items-center gap-3 my-3">
          <div class="flex-1 h-px bg-n-weak" />
          <span
            class="px-3 py-1 text-xs font-semibold border rounded-full first-letter:uppercase text-n-slate-11 border-n-weak bg-n-solid-1"
          >
            {{ dayLabel(item.unix, t, locale) }}
          </span>
          <div class="flex-1 h-px bg-n-weak" />
        </div>
        <MessageItem
          v-else
          :message="item.message"
          :continued="item.continued"
          :can-pin="canPin"
          :can-reply="canReply"
          :status="statusFor(item.message.user_id ?? item.message.user?.id)"
          :data-message-id="item.message.id"
          :highlighted="highlightedId === item.message.id"
          :current-user-id="currentUserId"
          :name-for="nameFor"
          :receipts="receipts"
          @info="emit('info', $event)"
          @star="emit('star', $event)"
          @pin="emit('pin', $event)"
          @react="(message, emoji) => emit('react', message, emoji)"
          @reply="emit('reply', $event)"
          @jump-to="jumpTo"
        />
      </template>
    </div>
  </div>
</template>
